import { useEffect, useRef } from 'react';

interface MobileOverflowSheetProps {
  open: boolean;
  onClose: () => void;
  onRegenerate: () => void;
  onPreview: () => void;
  onHistory: () => void;
  onDashboard: () => void;
  onDownloadPdf: () => void;
  onSharePdf: () => void;
  onOrderBook: () => void;
}

export function MobileOverflowSheet({
  open,
  onClose,
  onRegenerate,
  onPreview,
  onHistory,
  onDashboard,
  onDownloadPdf,
  onSharePdf,
  onOrderBook,
}: MobileOverflowSheetProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // WR-03: Move focus into dialog on open
  useEffect(() => {
    if (open) {
      const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }
  }, [open]);

  // WR-03: Trap Tab focus within dialog + WR-02: close on Escape
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key === 'Tab' && dialogRef.current) {
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hasAttribute('disabled'));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  };

  if (!open) return null;

  const items: Array<{ label: string; action: () => void }> = [
    { label: 'Regenerate Book', action: () => { onRegenerate(); onClose(); } },
    { label: 'Preview Book', action: () => { onPreview(); onClose(); } },
    { label: 'Version History', action: () => { onHistory(); onClose(); } },
    { label: 'Download PDF', action: () => { onDownloadPdf(); onClose(); } },
    { label: 'Share PDF', action: () => { onSharePdf(); onClose(); } },
    { label: 'Order Printed Book', action: () => { onOrderBook(); onClose(); } },
    { label: 'Back to Dashboard', action: () => { onDashboard(); onClose(); } },
  ];

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        aria-hidden="true"
        onClick={onClose}
      />
      {/* Sheet */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="More options"
        onKeyDown={handleKeyDown}
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-xl"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {items.map(item => (
          <button
            key={item.label}
            type="button"
            onClick={item.action}
            className="w-full text-left px-6 py-4 text-base text-stone-800 border-b border-stone-100 min-h-[56px] hover:bg-stone-50 transition-colors"
          >
            {item.label}
          </button>
        ))}
        <button
          type="button"
          onClick={onClose}
          className="w-full text-left px-6 py-4 text-base text-stone-500 min-h-[56px] hover:bg-stone-50 transition-colors"
        >
          Close
        </button>
      </div>
    </>
  );
}
